/**
 * 一括登録・一括更新の履歴。行ごとの結果まで残す(**パスワードは残さない**)。
 *
 * 実体は `idp_bulk_jobs`(Keycloak が持たないのでスイート側に持つ)。
 */
import { Alert, Card, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { BulkJob, BulkRow } from "../../lib/api";
import { errorText, useIdpBulkJobs } from "./hooks";
import { BULK_DONE, BULK_RUNNING, ROW_ERROR } from "./userOptions";

const statusColor: Record<string, string> = {
  [BULK_RUNNING]: "processing",
  [BULK_DONE]: "green",
  failed: "red",
  aborted: "orange",
};

const rowColumns: ColumnsType<BulkRow> = [
  { title: "行", dataIndex: "line", width: 90 },
  { title: "ユーザー名", dataIndex: "username", width: 220 },
  {
    title: "結果",
    dataIndex: "result",
    width: 110,
    render: (v: string) => <Tag color={v === ROW_ERROR ? "red" : "green"}>{v}</Tag>,
  },
  { title: "内容", width: 320, ellipsis: true, dataIndex: "message" },
];

export function BulkJobsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const query = useIdpBulkJobs({ limit: pageSize, offset: (page - 1) * pageSize });
  const running = (query.data?.items ?? []).some((j) => j.status === BULK_RUNNING);

  const columns: ColumnsType<BulkJob> = [
    { title: "日時", dataIndex: "createdAt", width: 200, render: (v: string | null) => v ?? "—" },
    { title: "種類", dataIndex: "kindLabel", width: 120, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: "状態",
      dataIndex: "statusLabel",
      width: 110,
      render: (v: string, job: BulkJob) => (
        <Tag color={statusColor[job.status] ?? "default"}>{v}</Tag>
      ),
    },
    { title: "ファイル", width: 240, dataIndex: "filename" },
    { title: "実行者", dataIndex: "actor", width: 160 },
    { title: "全体", dataIndex: "total", width: 90 },
    {
      title: "済み",
      dataIndex: "processed",
      width: 140,
      render: (v: number, job: BulkJob) =>
        job.status === BULK_RUNNING ? `${v} / ${job.total}` : v,
    },
    { title: "成功", dataIndex: "ok", width: 90 },
    {
      title: "失敗",
      dataIndex: "failed",
      width: 110,
      render: (v: number) => (v > 0 ? <Tag color="red">{v}</Tag> : v),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>一括登録の履歴</Typography.Title>
      <Typography.Paragraph type="secondary">
        行を開くと 1 行ごとの結果が出る。初期パスワードは残していない
        (メールで送れなかったときは、登録した直後の画面でしか見られない)。
        実行中のジョブは 20 行ごとに進み具合が更新される。
        <b>やり直す口は無い</b>:失敗した行を CSV で直して、もう一度入れ直す。
      </Typography.Paragraph>

      {running && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="実行中の取込がある"
          description="終わるまで自動で更新する(閉じても処理は続く)。"
        />
      )}

      {query.data?.items?.some((j) => j.message) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="途中で終わった取込がある"
          description={(query.data?.items ?? [])
            .filter((j) => j.message)
            .map((j) => `#${j.id}: ${j.message}`)
            .join(" / ")}
        />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="履歴を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<BulkJob>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          loading={query.isFetching}
          columns={columns}
          dataSource={query.data?.items ?? []}
          locale={{ emptyText: "まだ一括登録していない" }}
          expandable={{
            expandedRowRender: (job) => (
              <Table<BulkRow>
                scroll={{ x: "max-content" }}
                rowKey="line"
                size="small"
                columns={rowColumns}
                dataSource={job.rows ?? []}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `全 ${t} 件`,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>
    </div>
  );
}
